import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { patientApi } from '../../api/patientApi';

// Async thunks
export const fetchFeedbackHistory = createAsyncThunk(
  'feedback/fetchHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getFeedbackHistory();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch feedback history');
    }
  }
);

export const submitFeedback = createAsyncThunk(
  'feedback/submit',
  async ({ doctorId, feedbackData }, { rejectWithValue }) => {
    try {
      const response = await patientApi.submitFeedback(doctorId, feedbackData);
      return { doctorId, feedbackData, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to submit feedback');
    }
  }
);

const initialState = {
  history: {
    data: [],
    loading: false,
    error: null,
    pagination: null
  },
  submitting: false,
  submitError: null,
  submitSuccess: false
};

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    clearSubmitSuccess: (state) => {
      state.submitSuccess = false;
    },
    resetFeedbackState: (state) => {
      state.submitting = false;
      state.submitError = null;
      state.submitSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch feedback history
      .addCase(fetchFeedbackHistory.pending, (state) => {
        state.history.loading = true;
        state.history.error = null;
      })
      .addCase(fetchFeedbackHistory.fulfilled, (state, action) => {
        state.history.loading = false;
        state.history.data = action.payload.results;
        state.history.pagination = action.payload.pagination;
      })
      .addCase(fetchFeedbackHistory.rejected, (state, action) => {
        state.history.loading = false;
        state.history.error = action.payload;
      })
      // Submit feedback
      .addCase(submitFeedback.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        
        // Add the new feedback to history
        const { doctorId, feedbackData, response } = action.payload;
        const newFeedback = {
          id: response.feedback_id,
          doctor: {
            id: doctorId,
            name: 'Doctor', // This would be populated from the response in a real implementation
            specialization: null
          },
          rating: feedbackData.rating,
          comment: feedbackData.comment,
          created_at: new Date().toISOString()
        };
        
        state.history.data.unshift(newFeedback);
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
        state.submitSuccess = false;
      });
  }
});

export const { clearSubmitError, clearSubmitSuccess, resetFeedbackState } = feedbackSlice.actions;
export default feedbackSlice.reducer;
